import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { appointmentFormConfig } from '../config/appointmentFormConfigs';
import { FormField, commonFormFields } from '../types/formFields';
import './AdditionalInfoStep.scss';

interface AdditionalInfoStepProps {
  onNext: (data: Record<string, any>) => void;
  initialData?: Record<string, any>;
  setStepValidity?: (isValid: boolean) => void;
  formType: string;
  isPrivateAppointment?: boolean;
}

export const AdditionalInfoStep: React.FC<AdditionalInfoStepProps> = ({
  onNext,
  initialData = {},
  setStepValidity,
  formType,
  isPrivateAppointment = false,
}) => {
  const { t } = useTranslation('appointments');

  // Only handle appointment form types
  // Exclude: event types (starting with 'event-'), 'special-event', and 'unknown-appointment'
  const isEventType = formType?.startsWith('event-') || formType === 'special-event';
  const isUnknownType = formType === 'unknown-appointment';
  
  const formConfig = (isEventType || isUnknownType) ? {
    fields: [],
    layout: { rows: [] }
  } : appointmentFormConfig[formType as keyof typeof appointmentFormConfig] || {
    fields: [],
    layout: { rows: [] }
  };
  
  const commonFields = Object.keys(commonFormFields);
  const fields = (formConfig.fields?.filter((field: FormField) => 
    !commonFields.includes(field.id)
  ) || []).map((field: FormField) => {
    // For private appointments, make all fields optional
    if (isPrivateAppointment) {
      return { ...field, required: false };
    }
    return field;
  });
  
  // Form validation state
  const [formValues, setFormValues] = useState<Record<string, any>>({...initialData});
  const [formValid, setFormValid] = useState(false);

  // Validate the form whenever values change
  useEffect(() => {
    validateForm();
  }, [formValues]);

  // Update step validity whenever formValid changes
  useEffect(() => {
    if (setStepValidity) {
      setStepValidity(formValid);
    }
  }, [formValid, setStepValidity]);

  // Validate all required fields
  const validateForm = () => {
    // If there are no required fields, the form is valid
    if (!fields.some(field => field.required)) {
      setFormValid(true);
      return;
    }

    // Get all required field IDs
    const requiredFields = fields
      .filter(field => field.required)
      .map(field => field.id);

    // Check if all required fields have values
    const allRequiredFieldsHaveValues = requiredFields.every(fieldId => {
      return formValues[fieldId] && formValues[fieldId].trim() !== '';
    });

    setFormValid(allRequiredFieldsHaveValues);
  };
  
  // Handle input changes to update validation state
  const handleInputChange = (fieldId: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries());
    onNext(data);
  };

  return (
    <form id="current-step-form" onSubmit={handleSubmit} className="form-step">
      <div className="form-step__content">
        <div className="form-step__fields">
          {formConfig.layout?.rows.map((row: string[], rowIndex: number) => {
            const rowFields = row.filter(fieldId => !commonFields.includes(fieldId));
            if (rowFields.length === 0) return null;

            return (
              <div key={rowIndex} className={`form-row ${rowFields.length === 1 ? 'full-width' : ''}`}>
                {rowFields.map(fieldId => {
                  const field = fields.find(f => f.id === fieldId);
                  if (!field) {
                    return null;
                  }

                  return (
                    <div key={field.id} className={`form-field ${field.type === 'radio' ? 'form-field--with-radios' : ''} ${field.id === 'tradeProfessionals' ? 'trade' : ''}`}>
                      <label className="form-field__label" htmlFor={field.id}>
                        {t(field.translationKey)}{field.required ? '*' : ''}
                      </label>
                      {field.type === 'radio' ? (
                        <div className="form-field__question-row">
                          <div className="radio-group radio-group--left">
                            {field.options?.map((option: { label: string; value: string }) => (
                              <label key={option.value} className="radio-option">
                                <input
                                  type="radio"
                                  name={field.id}
                                  value={option.value}
                                  defaultChecked={initialData[field.id] === option.value}
                                  required={field.required}
                                  onChange={(e) => handleInputChange(field.id, e.target.checked ? option.value : '')}
                                />
                                <span>{t(option.label)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          id={field.id}
                          name={field.id}
                          className={`form-field__input form-field__textarea ${field.id === 'additionalComments' ? 'form-field__textarea--comments' : ''}`}
                          defaultValue={initialData[field.id]}
                          required={field.required}
                          onChange={(e) => handleInputChange(field.id, e.target.value)}
                        />
                      ) : field.type === 'select' ? (
                        field.component ? (
                          <>
                            <field.component
                              value={initialData[field.id]}
                              onChange={(value: string) => {
                                // Update the hidden input value
                                const formEl = document.getElementById(`hidden-${field.id}`) as HTMLInputElement;
                                if (formEl) {
                                  formEl.value = value || '';
                                }
                                
                                // Also update the initialData object to maintain state
                                initialData[field.id] = value;
                                
                                // Update validation state
                                handleInputChange(field.id, value || '');
                              }}
                              isClearable={field.isClearable}
                              placeholder={field.placeholder}
                            />
                            <input
                              type="hidden"
                              id={`hidden-${field.id}`}
                              name={field.id}
                              defaultValue={initialData[field.id]}
                            />
                          </>
                        ) : (
                          <select
                            id={field.id}
                            name={field.id}
                            className="form-field__input"
                            defaultValue={initialData[field.id]}
                            required={field.required}
                            onChange={(e) => handleInputChange(field.id, e.target.value)}
                          >
                            {field.options?.map((option: { label: string; value: string }) => (
                              <option key={option.value} value={option.value}>
                                {t(option.label)}
                              </option>
                            ))}
                          </select>
                        )
                      ) : (
                        <input
                          type={field.type}
                          id={field.id}
                          name={field.id}
                          className="form-field__input"
                          defaultValue={initialData[field.id]}
                          required={field.required}
                          onChange={(e) => handleInputChange(field.id, e.target.value)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </form>
  );
};
